import React, { useEffect, useMemo, useState } from 'react';

import {
  Divider,
  Input,
  Label,
  Modal,
  Select,
  Text,
  type TypeSelectOption,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';
import { isDeepEqual } from '@thefarmersfront/kitchen-utensil';

import { useGetSalesChannelCodesQuery, usePostSalesChannelMutation, usePutSalesChannelMutation } from '~/hooks/product';
import { transformToSelectOptions } from '~/utils/common';

import type { TypeInputState } from '~/types/common.types';
import type { TypeMappingProduct, TypeProductSalesChannel } from '~/types/product.types';

import styles from './ProductSalesModal.module.scss';

interface TypeProductSalesModalProps {
  open: boolean;
  mode: 'register' | 'detail';
  data: TypeMappingProduct | null;
  onCancel: () => void;
  onConfirm: () => void;
}

const ProductSalesModal = ({ open, mode, data, onCancel, onConfirm }: TypeProductSalesModalProps) => {
  const [salesChannelDateBeforeUserInput, setSalesChannelDateBeforeUserInput] =
    useState<TypeProductSalesChannel | null>(null);
  const [salesChannelData, setSalesChannelData] = useState<TypeProductSalesChannel | null>(null);

  const salesChannelCodesQuery = useGetSalesChannelCodesQuery({ enabled: open });

  const salesChannelOption = useMemo(
    () => transformToSelectOptions(salesChannelCodesQuery.data?.data ?? [], 'salesChannelCode', 'salesChannelName'),
    [salesChannelCodesQuery.data]
  );

  const registerSalesChannelMutation = usePostSalesChannelMutation();
  const updateSalesChannelMutation = usePutSalesChannelMutation();

  useEffect(() => {
    const initSalesChannelData = {
      skuId: data?.skuId ?? 0,
      skuName: data?.skuName ?? '',
      skuCode: data?.skuCode ?? '',
      ...(mode === 'register'
        ? {
            salesChannelId: 0,
            salesChannelCode: '',
            salesChannelName: null,
            salesChannelSkuCode: '',
            salesChannelSkuName: '',
          }
        : {
            salesChannelId: data?.salesChannelId ?? 0,
            salesChannelCode: data?.salesChannelCode ?? '',
            salesChannelName:
              data?.salesChannelName !== undefined && data?.salesChannelCode !== undefined
                ? { text: data.salesChannelName, value: data.salesChannelCode }
                : null,
            salesChannelSkuCode: data?.salesChannelSkuCode ?? '',
            salesChannelSkuName: data?.salesChannelSkuName ?? '',
          }),
    };

    setSalesChannelData({ ...initSalesChannelData });
    setSalesChannelDateBeforeUserInput({ ...initSalesChannelData });
  }, [data, mode]);

  const [salesInputStatus, setSalesInputStatus] = useState<{ [x: string]: TypeInputState }>({
    salesChannelCode: { status: 'default' },
    salesChannelName: { status: 'default' },
    salesChannelSkuCode: { status: 'default' },
    salesChannelSkuName: { status: 'default' },
  });

  const handleChangeSelect = (
    _: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    option: TypeSelectOption<string>
  ) => {
    if (!salesChannelData) {
      return;
    }

    setSalesChannelData({
      ...salesChannelData,
      salesChannelName: option,
      salesChannelCode: option.value,
    });

    setSalesInputStatus({
      ...salesInputStatus,
      salesChannelName: { status: 'default' },
      salesChannelCode: { status: 'default' },
    });
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (!salesChannelData) {
      return;
    }

    setSalesChannelData({
      ...salesChannelData,
      [name]: value,
    });

    const checkedInputStatus: { [x: string]: TypeInputState } = {
      ...salesInputStatus,
      [name]: { status: 'default' },
    };

    setSalesInputStatus(checkedInputStatus);
  };

  const handleBlurInput = (e: React.FocusEvent<HTMLTextAreaElement> | React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (!salesChannelData) {
      return;
    }

    setSalesChannelData({
      ...salesChannelData,
      [name]: value.trim(),
    });
  };

  const checkIsValidSalesChannelData = (data: TypeProductSalesChannel) => {
    const { salesChannelCode, salesChannelName, salesChannelSkuCode, salesChannelSkuName } = data;

    const checkedInputStatus: { [x: string]: TypeInputState } = {
      ...salesInputStatus,
      salesChannelName: salesChannelName ? { status: 'default' } : { status: 'error', message: '필수 선택입니다.' },
      salesChannelCode: salesChannelCode ? { status: 'default' } : { status: 'error', message: '필수 입력입니다.' },
      salesChannelSkuName: salesChannelSkuName
        ? { status: 'default' }
        : { status: 'error', message: '필수 입력입니다.' },
      salesChannelSkuCode: salesChannelSkuCode
        ? { status: 'default' }
        : { status: 'error', message: '필수 입력입니다.' },
    };

    setSalesInputStatus(checkedInputStatus);

    const isValidSalesChannelData = Object.values(checkedInputStatus).every((input) => input.status === 'default');

    return isValidSalesChannelData;
  };

  const handleClickRegister = () => {
    if (!salesChannelData) {
      toastService.addToast({
        type: 'error',
        message: '올바른 상품 정보가 아닙니다.\n상품 정보를 다시 불러와주세요.',
      });
      return;
    }

    const isInvalidProductData = salesChannelData.skuId === 0;

    if (isInvalidProductData) {
      toastService.addToast({
        type: 'error',
        message: '올바른 상품 정보가 아닙니다.\n상품 정보를 다시 불러와주세요.',
      });
      return;
    }

    if (!checkIsValidSalesChannelData(salesChannelData)) {
      return;
    }

    registerSalesChannelMutation.mutate(
      {
        skuId: salesChannelData.skuId,
        salesChannelCode: salesChannelData.salesChannelCode,
        salesChannelName: salesChannelData.salesChannelName?.text ?? '',
        salesChannelSkuCode: salesChannelData.salesChannelSkuCode,
        salesChannelSkuName: salesChannelData.salesChannelSkuName,
      },
      {
        onSuccess: () => {
          toastService.addToast({
            message: '상품 매핑 정보가 등록되었습니다.',
          });
          onConfirm?.();
        },
      }
    );
  };

  const handleClickUpdate = () => {
    if (!salesChannelData) {
      toastService.addToast({
        type: 'error',
        message: '올바른 상품 정보가 아닙니다.\n상품 정보를 다시 불러와주세요.',
      });
      return;
    }

    const isInvalidProductData = salesChannelData.salesChannelId === 0;

    if (isInvalidProductData) {
      toastService.addToast({
        type: 'error',
        message: '올바른 판매처 정보가 아닙니다.',
      });
      return;
    }

    if (!checkIsValidSalesChannelData(salesChannelData)) {
      return;
    }

    updateSalesChannelMutation.mutate(
      {
        salesChannelId: salesChannelData.salesChannelId,
        salesChannelCode: salesChannelData.salesChannelCode,
        salesChannelName: salesChannelData.salesChannelName?.text ?? '',
        salesChannelSkuCode: salesChannelData.salesChannelSkuCode,
        salesChannelSkuName: salesChannelData.salesChannelSkuName,
      },
      {
        onSuccess: () => {
          toastService.addToast({
            message: '상품 매핑 정보가 수정되었습니다.',
          });
          onConfirm?.();
        },
      }
    );
  };

  const handleClickCancel = () => {
    const isInputChanged = !isDeepEqual(salesChannelDateBeforeUserInput, salesChannelData);

    if (!isInputChanged) {
      onCancel?.();

      return;
    }

    dialogService.addDialog({
      type: 'confirm',
      children: (
        <span>
          {mode === 'register' ? '작성' : '수정'}된 내용이 저장되지 않습니다.
          <br />
          페이지를 나가시겠습니까?
        </span>
      ),
      confirmText: '나가기',
      onConfirm: () => onCancel?.(),
    });
  };

  return (
    <Modal
      open={open}
      title={mode === 'register' ? '상품 판매처 등록' : '상품 판매처 상세'}
      confirmText={mode === 'register' ? '등록' : '저장'}
      size="xlarge"
      onCancel={handleClickCancel}
      onConfirm={mode === 'register' ? handleClickRegister : handleClickUpdate}
    >
      <div className={styles['content-wrapper']}>
        <div className={styles['section']}>
          <Text className={styles['section-title']} size="xxlarge" weight="bold">
            컬리 상품 정보
          </Text>
          <div className={styles['input-row']}>
            <Label className={styles['label']} title="상품명" />
            <div className={styles['input-wrapper']}>
              <Input
                className={styles['input']}
                type="text"
                size="small"
                name="skuName"
                value={salesChannelData?.skuName}
                disabled
                placeholder="입력"
              />
            </div>
          </div>
          <div className={styles['input-row']}>
            <Label className={styles['label']} title="상품 코드" />
            <div className={`${styles['input-wrapper']}`}>
              <Input
                className={styles['input']}
                type="text"
                size="small"
                name="skuCode"
                value={salesChannelData?.skuCode}
                disabled
                placeholder="입력"
              />
            </div>
          </div>
        </div>
        <div className={styles['section']}>
          <Text className={styles['section-title']} size="xxlarge" weight="bold">
            판매처 상품 정보
          </Text>
          <div className={styles['input-row']}>
            <Label className={styles['label']} title="판매처명" required />
            <div className={styles['input-wrapper']}>
              <Select
                maxWidth="309px"
                size="small"
                placeholder="선택"
                options={salesChannelOption}
                value={salesChannelData?.salesChannelName ?? null}
                status={salesInputStatus.salesChannelName.status}
                validationMessage={salesInputStatus.salesChannelName.message}
                onChange={handleChangeSelect}
              />
            </div>
          </div>
          <div className={styles['input-row']}>
            <Label className={styles['label']} title="판매처 코드" required />
            <div className={`${styles['input-wrapper']}`}>
              <Input
                className={styles['input']}
                maxWidth="309px"
                placeholder="판매처 선택 시 자동 입력"
                type="text"
                size="small"
                name="salesChannelCode"
                value={salesChannelData?.salesChannelCode ?? ''}
                disabled
                status={salesInputStatus.salesChannelCode.status}
                validationMessage={salesInputStatus.salesChannelCode.message}
              />
            </div>
          </div>
          <div className={styles['divider-wrapper']}>
            <Divider color="gray100" />
          </div>
          <div className={styles['input-row']}>
            <Label className={styles['label']} title="판매처 상품명" required />
            <div className={styles['input-wrapper']}>
              <Input
                className={styles['input']}
                maxWidth="550px"
                placeholder="입력"
                type="text"
                size="small"
                name="salesChannelSkuName"
                value={salesChannelData?.salesChannelSkuName ?? undefined}
                count
                maxLength={40}
                status={salesInputStatus.salesChannelSkuName.status}
                validationMessage={salesInputStatus.salesChannelSkuName.message}
                onChange={handleChangeInput}
                onBlur={handleBlurInput}
              />
            </div>
          </div>
          <div className={styles['input-row']}>
            <Label className={styles['label']} title="판매처 상품코드" required />
            <div className={styles['input-wrapper']}>
              <Input
                className={styles['input']}
                maxWidth="550px"
                placeholder="입력"
                type="text"
                size="small"
                name="salesChannelSkuCode"
                value={salesChannelData?.salesChannelSkuCode ?? undefined}
                count
                maxLength={20}
                status={salesInputStatus.salesChannelSkuCode.status}
                validationMessage={salesInputStatus.salesChannelSkuCode.message}
                onChange={handleChangeInput}
                onBlur={handleBlurInput}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProductSalesModal;
